/**
 * Central navigation config -- single source of truth for all pages/tools.
 *
 * Consumed by: NavBar, CockpitHome, FeedbackFAB, Sidebar, SecondaryNav
 *
 * Structure (v2 — March 2026):
 *   Sidebar: Home, Vandaag, Planning, Projecten, Accounts, Productie, AI, Operations
 *   Bottom:  Feedback, Lab (gated), Admin (gated)
 */
import HomeIcon from "@mui/icons-material/Home";
import TodayIcon from "@mui/icons-material/Today";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PercentIcon from "@mui/icons-material/Percent";
import PersonIcon from "@mui/icons-material/Person";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import StorageIcon from "@mui/icons-material/Storage";
import BookIcon from "@mui/icons-material/Book";
import BusinessIcon from "@mui/icons-material/Business";
import GridViewIcon from "@mui/icons-material/GridView";
import DescriptionIcon from "@mui/icons-material/Description";
import SubtitlesIcon from "@mui/icons-material/Subtitles";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/Send";
import BadgeIcon from "@mui/icons-material/Badge";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import { FORMAAT_OMZETTEN_URL } from "./flowUrl";
import GroupsIcon from "@mui/icons-material/Groups";
import MovieIcon from "@mui/icons-material/Movie";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ChatIcon from "@mui/icons-material/Chat";
import BuildIcon from "@mui/icons-material/Build";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import VideocamIcon from "@mui/icons-material/Videocam";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import StarIcon from "@mui/icons-material/Star";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CampaignIcon from "@mui/icons-material/Campaign";
import InboxIcon from "@mui/icons-material/MoveToInbox";
import ArticleIcon from "@mui/icons-material/Article";
import FeedbackIcon from "@mui/icons-material/Feedback";
import LanguageIcon from "@mui/icons-material/Language";
import SettingsIcon from "@mui/icons-material/Settings";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TimerIcon from "@mui/icons-material/Timer";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import MovieFilterIcon from "@mui/icons-material/MovieFilter";
import BugReportIcon from "@mui/icons-material/BugReport";
import TvIcon from "@mui/icons-material/Tv";
import ScienceIcon from "@mui/icons-material/Science";
import TheatersIcon from "@mui/icons-material/Theaters";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import ExploreIcon from "@mui/icons-material/Explore";
import HandshakeIcon from "@mui/icons-material/Handshake";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

// -- Commonly referenced paths ------------------------------------------------
export const PATHS = {
  meetings: "/MeetingDashboard",
  materials: "/MaterialReservation",
  srtGenerator: "/SRTGenerator",
  ritmo: "/ritmo",
  srtDashboard: "/SRTDashboard",
  dashboard: "/dashboard",
  projects: "/projects",
};

// -- Work-audience mapping (mirrors RoleDashboard.jsx ROLE_MAPPING) -----------
const WORK_AUDIENCE_MAPPING = {
  PL: ["Projectleider"],
  UITVOERDER: ["Editor", "Animator", "Cameraman", "Camera Operator", "Colorist / Grader", "Geluidstechnicus", "Illustrator"],
  CREATIVE: ["Regisseur", "Creatief Strateeg", "Art Director", "Copywriter"],
  PRODUCER: ["Producer", "Productie-assistent", "Opnameleider", "Planner"],
  MANAGEMENT: ["MT", "Accountmanager", "HR", "Boekhouder"],
};

export function resolveWorkAudience(functions) {
  const arr = Array.isArray(functions)
    ? functions
    : typeof functions === "string" && functions
      ? functions.split(", ")
      : [];
  if (arr.length === 0) return null;
  for (const [audience, funcs] of Object.entries(WORK_AUDIENCE_MAPPING)) {
    if (arr.some((f) => funcs.includes(f))) return audience;
  }
  return null;
}

/**
 * NAV_ITEMS -- every navigable page/tool in the app.
 */
export const NAV_ITEMS = [
  // -- Core navigation --------------------------------------------------------
  { path: "/", label: "Home", icon: HomeIcon, quickNav: { roles: ["*"] }, hidden: true },
  { path: "/vandaag", label: "Vandaag", icon: TodayIcon, quickNav: { roles: ["*"] } },
  { path: "/dashboard", label: "Mijn Dashboard", icon: PersonIcon, featured: true, quickNav: { roles: ["*"] }, audiences: ["PL", "UITVOERDER", "CREATIVE", "PRODUCER", "MANAGEMENT"] },

  // -- Planning ---------------------------------------------------------------
  { path: "/slim-uren", label: "Slim Uren", icon: TimerIcon, category: "Planning", quickNav: { roles: ["*"] }, audiences: ["PL", "UITVOERDER", "CREATIVE", "PRODUCER", "MANAGEMENT"] },
  { path: "/mail-assistent", label: "Frits (mail-assistent)", icon: FeedbackIcon, category: "Planning", audiences: ["PL", "MANAGEMENT"] },
  { path: "/ai-mail", label: "AI-mail", icon: FeedbackIcon, category: "Planning", audiences: ["MANAGEMENT"] },
  { path: "/dagstart", label: "Dagstart", icon: CalendarViewWeekIcon, category: "Planning", featured: true, audiences: ["PL", "UITVOERDER", "CREATIVE", "PRODUCER"] },
  { path: "/planning/overzicht-per-project", label: "Planningsgrid per project", icon: ViewWeekIcon, category: "Planning", audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  { path: "/planning/weekoverzicht-pl", label: "Weekoverzicht per PL", icon: CalendarViewWeekIcon, category: "Planning", audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  { path: "/weekplan", label: "Weekplan", icon: CalendarViewWeekIcon, category: "Planning", audiences: ["PL", "UITVOERDER", "CREATIVE", "PRODUCER", "MANAGEMENT"] },
  { path: "/overdracht", label: "Overdracht (PL)", icon: SwapHorizIcon, category: "Planning", audiences: ["PL", "MANAGEMENT"] },
  { path: "/leveranciersbeoordelingen", label: "Leveranciersbeoordelingen", icon: StarIcon, category: "Planning", audiences: ["PL", "MANAGEMENT"] },
  { path: "/task-board", label: "Weekbord", icon: ViewWeekIcon, category: "Planning", quickNav: { roles: ["*"] }, audiences: ["PL", "UITVOERDER", "CREATIVE"] },
  { path: "/capacity", label: "Capaciteit", icon: GridViewIcon, category: "Planning", audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  { path: "/planner-todo", label: "Planner To-Do", icon: AssignmentIndIcon, category: "Planning", feature: "planner_todo", quickNav: { roles: ["admin", "management", "projectleider"] }, audiences: ["PL", "PRODUCER"] },
  { path: "/planning/inplannen", label: "Nog in te plannen", icon: AssignmentIndIcon, category: "Planning", feature: "planner_todo", audiences: ["PL", "PRODUCER"] },
  { path: "/slimme-toewijzing", label: "Slimme toewijzing", icon: AssignmentIndIcon, category: "Planning", feature: "planner_todo", audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  { path: "/draaidag-planner", label: "Draaidag-planner", icon: AssignmentIndIcon, category: "Planning", feature: "planner_todo", audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  { path: "/MaterialReservation", label: "Materialen reserveren", icon: BookIcon, category: "Planning", featured: true, audiences: ["PRODUCER"] },
  { path: "/planning/corvee", label: "Corvee", icon: CleaningServicesIcon, category: "Planning", audiences: ["PL", "UITVOERDER", "CREATIVE", "PRODUCER", "MANAGEMENT"] },

  // -- Projecten (no category — direct sidebar entry) -------------------------
  { path: "/projects", label: "Projecten", icon: FolderOpenIcon, featured: true, quickNav: { roles: ["*"] }, audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  // Notes: supersnel een notitie ("iemand belt") — koppelen aan klant/project kan later.
  { path: "/notes", label: "Notities", icon: StickyNote2Icon, featured: true, quickNav: { roles: ["*"] }, audiences: ["PL", "PRODUCER", "MANAGEMENT"] },

  // -- Accounts (hub with commercial/content tools) ---------------------------
  { path: "/marketing", label: "Marketing OS", icon: CampaignIcon, category: "Accounts", audiences: ["CREATIVE", "MANAGEMENT"] },
  { path: "/aanvragen-portaal", label: "Klantaanvragen", icon: InboxIcon, category: "Accounts", audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  { path: "/contentkalender", label: "Contentkalender", icon: EventNoteIcon, category: "Accounts", audiences: ["CREATIVE", "PL", "MANAGEMENT"] },
  { path: "/website-preview", label: "Website Preview", icon: LanguageIcon, category: "Accounts", feature: "website_feedback" },
  { path: "/sharepoint-analyse", label: "SharePoint opslag", icon: StorageIcon, category: "Operations", feature: "sharepoint_analyse" },

  // -- Productie (new hub) ----------------------------------------------------
  { path: "/callsheets", label: "Callsheets", icon: DescriptionIcon, category: "Productie", audiences: ["PRODUCER"] },
  { path: "/script-breakdown", label: "Script Breakdown", icon: MovieFilterIcon, category: "Productie", audiences: ["UITVOERDER", "CREATIVE"] },
  { path: "/video-qa", label: "Video Edit QA", shortLabel: "Edit QA", icon: BugReportIcon, category: "Productie", audiences: ["UITVOERDER", "PL"] },
  { path: "/SRTGenerator", label: "SRT Generator", icon: SubtitlesIcon, category: "Productie", audiences: ["UITVOERDER"] },
  { path: "/ritmo", label: "Ondertitel Studio", shortLabel: "Ondertitels", icon: MovieFilterIcon, category: "Productie", audiences: ["PL", "UITVOERDER", "CREATIVE", "PRODUCER", "MANAGEMENT"] },
  { path: "/SRTDashboard", label: "SRT Beheer", icon: DashboardIcon, category: "Productie", audiences: ["UITVOERDER"] },
  { path: "/video-downloader", label: "Video Downloader", shortLabel: "Downloader", icon: DownloadIcon, category: "Productie", audiences: ["UITVOERDER"] },
  { path: "/name-title-generator", label: "Titel Generator", shortLabel: "Titels", icon: BadgeIcon, category: "Productie", audiences: ["UITVOERDER", "CREATIVE"] },
  // Woont in Flow (Full Frame-account): opent in een nieuw tabblad; /formaat-omzetten stuurt door.
  { path: "/formaat-omzetten", label: "Formaat omzetten", shortLabel: "Formaat", icon: AspectRatioIcon, category: "Productie", externalUrl: FORMAAT_OMZETTEN_URL, audiences: ["UITVOERDER", "CREATIVE"] },
  { path: "/pptx-video-replacer", label: "PowerPoint Video Replacer", shortLabel: "PPTX Replacer", icon: SlideshowIcon, category: "Productie", audiences: ["UITVOERDER", "CREATIVE"] },
  { path: "/narrowcasting", label: "Narrowcasting", icon: TvIcon, category: "Productie", audiences: ["UITVOERDER", "CREATIVE"] },
  { path: "/pdf-combiner", label: "PDF Combiner", icon: DescriptionIcon, category: "Productie", audiences: ["PL", "PRODUCER", "MANAGEMENT"] },
  { path: "/SRTomzettenFCP", label: "SRT naar FCP", icon: SubtitlesIcon, hidden: true },

  // -- AI ---------------------------------------------------------------------
  { path: "/ai-chat", label: "AI chat project", icon: ChatIcon, category: "AI" },
  { path: "/gpt-maker", label: "GPT Bouwer", icon: BuildIcon, category: "AI", audiences: ["CREATIVE"] },
  { path: "/explainer-script", label: "Explainer Studio", shortLabel: "Explainer", icon: AutoFixHighIcon, category: "AI", audiences: ["CREATIVE"] },
  { path: "/ai-beelden", label: "AI-beelden (Higgsfield)", shortLabel: "AI-beelden", icon: AutoFixHighIcon, category: "AI", audiences: ["CREATIVE", "PL"] },
  // Geen audiences: regisseurs én adviseurs moeten hier bij kunnen (Joris, 10 sep 2026).
  { path: "/scriptstijlen", label: "Scriptstijlen", icon: EditNoteIcon, category: "AI" },
  { path: "/MeetingDashboard", label: "Meetings", shortLabel: "Meetings", icon: GroupsIcon, category: "Mijn Werk", audiences: ["PL", "CREATIVE"] },
  { path: "/video-search", label: "Videotheek", icon: VideoLibraryIcon, category: "AI", audiences: ["UITVOERDER", "CREATIVE"] },
  { path: "/inspiration", label: "Inspiratie", icon: MovieIcon, category: "AI", audiences: ["CREATIVE"] },
  { path: "/effie-guide", label: "Effie Awards Gids", shortLabel: "Effie Gids", icon: EmojiEventsIcon, category: "AI", audiences: ["CREATIVE", "PL"] },
  { path: "/avatar-chat", label: "Avatar Chat", icon: VideocamIcon, category: "AI", audiences: ["CREATIVE"] },

  // -- Operations (management, gated) -----------------------------------------
  { path: "/sales-dashboard", label: "Sales Dashboard", shortLabel: "Sales", icon: TrendingUpIcon, category: "Operations", feature: "sales_dashboard", quickNav: { roles: ["admin", "management"] }, audiences: ["MANAGEMENT"] },
  { path: "/sales/prospects", label: "Sales Prospects", shortLabel: "Prospects", icon: HandshakeIcon, category: "Operations", feature: "sales_prospects", audiences: ["MANAGEMENT"] },
  { path: "/offertes", label: "Offerte Generator", shortLabel: "Offertes", icon: HandshakeIcon, category: "Operations", feature: "offertes", audiences: ["MANAGEMENT", "PL"] },
  { path: "/achmea-export", label: "Achmea factuur-export", shortLabel: "Achmea-export", icon: HandshakeIcon, category: "Operations", feature: "offertes", audiences: ["MANAGEMENT", "PL"] },
  { path: "/revenue-forecast", label: "Revenue Forecast", shortLabel: "Forecast", icon: ShowChartIcon, category: "Operations", feature: "revenue_forecast", audiences: ["MANAGEMENT"] },
  // Budget per account (27 sep 2026): targets volgend jaar + export naar het Revenue-blad — eigen recht "budget" (admin, management)
  { path: "/budget", label: "Budget", icon: ShowChartIcon, category: "Operations", feature: "budget", audiences: ["MANAGEMENT"] },
  { path: "/opdracht-mix", label: "Opdracht-mix", shortLabel: "Mix", icon: DashboardIcon, category: "Operations", feature: "kpi_dashboard", audiences: ["MANAGEMENT"] },
  { path: "/ohw-check", label: "OHW Check", shortLabel: "OHW", icon: FactCheckIcon, category: "Operations", feature: "kpi_dashboard", quickNav: { roles: ["admin", "management"] }, audiences: ["MANAGEMENT"] },
  { path: "/uren-check", label: "Uren Controle", shortLabel: "Uren", icon: ScheduleIcon, category: "Operations", feature: "kpi_dashboard", quickNav: { roles: ["admin", "management"] }, audiences: ["MANAGEMENT"] },
  { path: "/enquete-dashboard", label: "Klanttevredenheid", icon: StarIcon, category: "Operations", feature: "enquete_dashboard", audiences: ["MANAGEMENT"] },
  // Freelancers · Mailadressen (28 sep): M365-adres per freelancer aanmaken/pauzeren/hervatten. Later een tab in HR & leveranciers.
  { path: "/freelancers/mailadressen", label: "Freelancers · Mailadressen", shortLabel: "Freelancer-mail", icon: BadgeIcon, category: "Operations", feature: "freelancer_mail", audiences: ["MANAGEMENT", "PL"] },
  // Leveranciers (23 okt): freelancers en bedrijven, alleen lezen, voor PL en management.
  { path: "/leveranciers", label: "Leveranciers", icon: HandshakeIcon, category: "Operations", feature: "leveranciers", audiences: ["PL", "MANAGEMENT"] },
  { path: "/strategy", label: "Strategie", icon: ExploreIcon, feature: "strategy", audiences: ["MANAGEMENT"] },
  // Werving (ATS, sprint 1 — 4 okt 2026): vacatures, sollicitaties uit ikwilwerken@, blind scoren — recht "werving" (MT; HR/teamleads via override)
  { path: "/werving", label: "Werving", icon: PersonSearchIcon, category: "Operations", feature: "werving", audiences: ["MANAGEMENT"] },

  // -- User / Admin -----------------------------------------------------------
  { path: "/mijn-profiel", label: "Mijn profiel", icon: PersonIcon, inUserMenu: true },
  { path: "/my-feedback", label: "Mijn Feedback", icon: FeedbackIcon, inUserMenu: true },
  // Wijzigingen testen (okt 2026): per pagina wat er veranderd is, test of live, teststatus + feedback — recht "wijzigingen" (admin, management)
  { path: "/wijzigingen", label: "Wijzigingen testen", icon: FactCheckIcon, feature: "wijzigingen", inUserMenu: true },
  { path: "/admin/settings", label: "Admin Instellingen", icon: SettingsIcon, feature: "admin_settings", inUserMenu: true },
  { path: "/joris-dashboard", label: "Werkdruk PL's & team", icon: DashboardIcon, category: "Operations", feature: "kpi_dashboard", quickNav: { roles: ["admin", "management"] }, audiences: ["MANAGEMENT"] },
  { path: "/declarabiliteit", label: "Declarabiliteit", icon: PercentIcon, category: "Operations", feature: "kpi_dashboard", audiences: ["MANAGEMENT"] },
  { path: "/declarabiliteit/persoon/:email", label: "Declarabiliteit per persoon", icon: PercentIcon, category: "Operations", feature: "kpi_dashboard", hidden: true },
  { path: "/sync-check", label: "Sync-check (Airtable ↔ Gripp)", icon: SyncProblemIcon, inUserMenu: true, emailAllowlist: ["joris@fullframe.nl"] },
  { path: "/lab", label: "Rick's Laboratorium", shortLabel: "Lab", icon: ScienceIcon, feature: "lab" },

  // -- Hub pages --------------------------------------------------------------
  { path: "/ai-hub", label: "AI", icon: AutoFixHighIcon },
  { path: "/accounts", label: "Accounts", icon: BusinessIcon },
  { path: "/planning", label: "Planning", icon: ViewWeekIcon },
  { path: "/productie", label: "Productie", icon: TheatersIcon },
  { path: "/operations", label: "Operations", icon: ShowChartIcon },

  // -- Dynamic sub-routes (hidden, for title/icon resolution) -----------------
  { path: "/accounts/:accountId/workspace", label: "Account Workspace", icon: BusinessIcon, hidden: true },
  { path: "/projects/:id", label: "Project Details", icon: FolderOpenIcon, hidden: true },
  { path: "/callsheets/:id", label: "Callsheet Bewerken", icon: DescriptionIcon, hidden: true },
  { path: "/MeetingDashboard/:id", label: "Meeting", icon: GroupsIcon, hidden: true },
  // Overlegmodule (20 sep 2026): Mijn week, overlegpagina, actieregister — zichtbaarheid per overleg op de server
  { path: "/overleg", label: "Overleggen", shortLabel: "Overleg", icon: EventNoteIcon, category: "Mijn Werk", feature: "overleg", featured: true, quickNav: { roles: ["*"] }, audiences: ["PL", "PRODUCER", "MANAGEMENT", "CREATIVE"] },
  { path: "/overleg/acties", label: "Actieregister", icon: EventNoteIcon, category: "Mijn Werk", feature: "overleg" },
  // Bestanden delen (okt 2026): intern een bestand naar collega's sturen — Voor mij / Verstuurd, 90 dagen
  { path: "/bestanden", label: "Bestanden delen", icon: SendIcon, category: "Mijn Werk", feature: "bestanden" },
  { path: "/overleg/sessie/:id", label: "Overleg", icon: EventNoteIcon, hidden: true },
  { path: "/account/:accountId", label: "Account Details", icon: TrendingUpIcon, hidden: true },
];

// -- Derived lookups ----------------------------------------------------------

const _pathMap = new Map();
NAV_ITEMS.forEach((item) => {
  if (!item.path.includes(":")) {
    _pathMap.set(item.path, item);
  }
});

const _dynamicItems = NAV_ITEMS.filter((item) => item.path.includes(":")).map(
  (item) => ({
    ...item,
    _regex: new RegExp("^" + item.path.replace(/:[^/]+/g, "[^/]+") + "$"),
  })
);

function _findItem(pathname) {
  const exact = _pathMap.get(pathname);
  if (exact) return exact;
  for (const item of _dynamicItems) {
    if (item._regex.test(pathname)) return item;
  }
  for (const [path, item] of _pathMap) {
    if (path !== "/" && pathname.startsWith(path + "/")) return item;
  }
  return null;
}

/** Get page title for a pathname. */
export function getPageTitle(pathname) {
  const item = _findItem(pathname);
  return item ? item.label : "Full Frame Tools";
}

/** Get page icon component class for a pathname. Caller renders: <Icon /> */
export function getPageIcon(pathname) {
  const item = _findItem(pathname);
  return item?.icon || null;
}

/** Resolve a tool name for any pathname (static + dynamic). */
export function resolveToolName(pathname) {
  const item = _findItem(pathname);
  return item ? item.label : null;
}

/** Get quickNav items for a given RBAC role, filtered by permissions. */
export function getQuickNav(role = "team", permissions = {}) {
  return NAV_ITEMS.filter((item) => {
    if (!item.quickNav) return false;
    const roles = item.quickNav.roles;
    if (!roles.includes("*") && !roles.includes(role)) return false;
    if (item.feature && !permissions[item.feature]?.view) return false;
    return true;
  }).map((item) => ({
    path: item.path,
    label: item.shortLabel || item.label,
    Icon: item.icon,
  }));
}

/** Get featured tools for CockpitHome. */
export function getFeaturedTools() {
  return NAV_ITEMS.filter((item) => item.featured && !item.hidden).map(
    (item) => ({
      to: item.path,
      title: item.shortLabel || item.label,
      Icon: item.icon,
    })
  );
}

/** Get tool categories for CockpitHome grid. */
export function getToolCategories(workAudience = null) {
  const catMap = new Map();
  const catOrder = [];

  NAV_ITEMS.forEach((item) => {
    if (!item.category || item.hidden) return;
    if (!catMap.has(item.category)) {
      catMap.set(item.category, { items: [], audienceScore: 0 });
      catOrder.push(item.category);
    }
    const cat = catMap.get(item.category);
    cat.items.push({
      to: item.path,
      title: item.shortLabel || item.label,
      Icon: item.icon,
      feature: item.feature || null,
    });
    if (workAudience && item.audiences?.includes(workAudience)) {
      cat.audienceScore += 1;
    }
  });

  const sorted = workAudience
    ? [...catOrder].sort((a, b) => {
        const scoreA = catMap.get(a).audienceScore;
        const scoreB = catMap.get(b).audienceScore;
        if (scoreB !== scoreA) return scoreB - scoreA;
        return catOrder.indexOf(a) - catOrder.indexOf(b);
      })
    : catOrder;

  return sorted.map((title) => {
    const { items } = catMap.get(title);
    const allGated = items.every((it) => it.feature);
    return {
      title,
      feature: allGated ? items[0].feature : null,
      items,
    };
  });
}

/** Get TOOL_NAMES mapping for FeedbackFAB. */
/** Paden + namen voor de toolkeuzelijst van de feedbackknop. Verborgen pagina's zonder
 *  parameter horen erbij: Home staat niet in het menu, maar je wilt er wel feedback over
 *  kunnen geven (tool-feedback #19/#27/#30). */
export function getToolNames() {
  const map = {};
  NAV_ITEMS.forEach((item) => {
    if (item.path.includes(":")) return;
    map[item.path] = item.label;
  });
  return map;
}

/** Get user menu items (avatar dropdown). */
export function getUserMenuItems(permissions = {}, userEmail = "") {
  const emailLower = (userEmail || "").toLowerCase();
  return NAV_ITEMS.filter((item) => {
    if (!item.inUserMenu) return false;
    if (item.feature && !permissions[item.feature]?.view) return false;
    if (item.emailAllowlist && !item.emailAllowlist.includes(emailLower)) return false;
    return true;
  }).map((item) => ({
    path: item.path,
    label: item.label,
    Icon: item.icon,
  }));
}

/** Get sidebar navigation items — 8 primary + bottom. */
export function getSidebarItems(permissions = {}, userEmail = "") {
  const isJoris = (userEmail || "").toLowerCase() === "joris@fullframe.nl";
  return {
    primary: [
      { path: '/', label: 'Home', Icon: HomeIcon },
      { path: '/mijn-werk', label: 'Mijn Werk', Icon: WorkOutlineIcon },
      { path: '/mijn-todos', label: "Privé-to-do's", Icon: PlaylistAddCheckIcon },
      { path: '/notes', label: 'Notities', Icon: StickyNote2Icon },
      { path: '/planning', label: 'Planning', Icon: ViewWeekIcon },
      { path: '/projects', label: 'Projecten', Icon: FolderOpenIcon },
      { path: '/accounts', label: 'Accounts', Icon: BusinessIcon },
      { path: '/productie', label: 'Productie', Icon: TheatersIcon },
      { path: '/ai-hub', label: 'AI', Icon: AutoFixHighIcon },
      ...(permissions.sales_dashboard?.view || permissions.kpi_dashboard?.view || permissions.strategy?.view
        ? [{ path: '/operations', label: 'Operations', Icon: ShowChartIcon }]
        : []),
      ...(permissions.lab?.view
        ? [{ path: '/lab', label: 'Lab', Icon: ScienceIcon }]
        : []),
    ],
    tools: [],
    toolsLabel: '',
    bottom: [
      { path: '/my-feedback', label: 'Feedback', Icon: FeedbackIcon },
      ...(permissions.admin_settings?.view
        ? [{ path: '/admin/settings', label: 'Admin', Icon: SettingsIcon }]
        : []),
      // Joris-only privé tools (MT Dashboard staat nu onder Operations)
      ...(isJoris
        ? [
            { path: '/sync-check', label: 'Sync-check', Icon: SyncProblemIcon },
          ]
        : []),
    ],
  };
}

// -- Secondary navigation (section sub-menus) ---------------------------------
// Visible when browsing any child page within the section.

export const SECONDARY_NAV_SECTIONS = {
  mijnwerk: {
    label: "Mijn Werk",
    hubPath: "/mijn-werk",
    paths: ["/mijn-werk", "/vandaag", "/dashboard", "/mijn-todos", "/mijn-taken", "/slim-uren", "/mail-assistent", "/ai-mail", "/weekplan", "/MeetingDashboard", "/overdracht", "/leveranciersbeoordelingen", "/overleg", "/overleg/acties", "/bestanden"],
    items: [
      { path: "/mijn-werk", label: "Overzicht" },
      { path: "/vandaag", label: "Vandaag", group: "Werkproces" },
      { path: "/overleg", label: "Overleggen", group: "Overleg", feature: "overleg", beta: true },
      { path: "/overleg/acties", label: "Actieregister", group: "Overleg", feature: "overleg", beta: true },
      { path: "/dashboard", label: "Mijn Dashboard", group: "Werkproces" },
      { path: "/mijn-taken", label: "Projecttaken", group: "Werkproces", beta: true },
      { path: "/mijn-todos", label: "Privé-to-do's", group: "Werkproces", beta: true },
      { path: "/weekplan", label: "Weekplan", group: "Werkproces", beta: true },
      { path: "/mail-assistent", label: "Frits (mail)", group: "Werkproces", beta: true, ai: true },
      { path: "/ai-mail", label: "AI-mail", group: "Werkproces", beta: true, ai: true },
      { path: "/slim-uren", label: "Slim Uren", group: "Werkproces", beta: true, ai: true },
      { path: "/MeetingDashboard", label: "Meetings", group: "Werkproces", ai: true },
      { path: "/overdracht", label: "Overdracht", group: "Werkproces", beta: true, ai: true },
      { path: "/bestanden", label: "Bestanden delen", group: "Werkproces", feature: "bestanden", beta: true },
    ],
  },
  planning: {
    label: "Planning",
    hubPath: "/planning",
    paths: ["/planning", "/planning/corvee", "/planning/voertuigen", "/planning/overzicht-per-project", "/planning/weekoverzicht-pl", "/planning/inplannen", "/dagstart", "/draaidagen", "/task-board", "/capacity", "/planner-todo", "/slimme-toewijzing", "/draaidag-planner", "/MaterialReservation", "/materiaal-beheer", "/materiaal-beheer/materialen", "/materiaal-beheer/sets"],
    items: [
      { path: "/planning", label: "Overzicht" },
      { path: "/dagstart", label: "Dagstart", group: "Werkproces", beta: true },
      { path: "/draaidagen", label: "Draaidagen", group: "Werkproces", beta: true },
      { path: "/planning/inplannen", label: "Nog in te plannen", feature: "planner_todo", group: "Werkproces", beta: true },
      { path: "/planning/overzicht-per-project", label: "Planningsgrid per project", group: "Werkproces", beta: true },
      { path: "/planning/weekoverzicht-pl", label: "Weekoverzicht per PL", group: "Werkproces", beta: true },
      { path: "/task-board", label: "Weekbord", group: "Werkproces", beta: true },
      { path: "/capacity", label: "Capaciteit", group: "Werkproces", beta: true },
      { path: "/planner-todo", label: "Planner To-Do", feature: "planner_todo", group: "Plannen", beta: true },
      { path: "/slimme-toewijzing", label: "Slimme toewijzing", feature: "planner_todo", group: "Plannen", beta: true },
      { path: "/draaidag-planner", label: "Draaidag-planner", feature: "planner_todo", group: "Plannen", beta: true },
      { path: "/MaterialReservation", label: "Materialen reserveren", group: "Resources" },
      { path: "/materiaal-beheer/materialen", label: "Materiaalbeheer", group: "Resources" },
      { path: "/planning/voertuigen", label: "Voertuigen & Brandstof", group: "Resources" },
      { path: "/planning/corvee", label: "Corvee", group: "Kantoor", beta: true },
    ],
  },
  accounts: {
    label: "Accounts",
    hubPath: "/accounts",
    paths: ["/accounts", "/marketing", "/aanvragen-portaal", "/contentkalender"],
    items: [
      { path: "/accounts", label: "Overzicht" },
      { path: "/aanvragen-portaal", label: "Klantaanvragen", group: "Werkproces", beta: true },
      { path: "/contentkalender", label: "Contentkalender", group: "Werkproces", beta: true },
      { path: "/marketing", label: "Marketing OS", group: "Werkproces", beta: true },
    ],
  },
  productie: {
    label: "Productie",
    hubPath: "/productie",
    paths: ["/productie", "/callsheets", "/script-breakdown", "/video-qa", "/ondertiteling", "/SRTGenerator", "/ritmo", "/SRTDashboard", "/video-downloader", "/name-title-generator", "/formaat-omzetten", "/pptx-video-replacer", "/narrowcasting", "/pdf-combiner", "/video-search", "/explainer-script", "/scriptstijlen", "/effie-guide", "/color-converter", "/qr-generator"],
    items: [
      { path: "/productie", label: "Overzicht" },
      { path: "/callsheets", label: "Callsheets", group: "Pre-productie" }, // FU-07: actieve tool — geen planned/beta-badge, anders standaard onzichtbaar
      { path: "/script-breakdown", label: "Script Breakdown", group: "Pre-productie", beta: true },
      { path: "/video-qa", label: "Edit QA", group: "Post-productie", beta: true },
      { path: "/ondertiteling", label: "Ondertiteling", group: "Post-productie", ai: true }, // uit beta (Joris, 6 sep)
      { path: "/name-title-generator", label: "Titels", group: "Post-productie", beta: true },
      { path: "/formaat-omzetten", label: "Formaat omzetten", group: "Post-productie", beta: true, externalUrl: FORMAAT_OMZETTEN_URL },
      { path: "/ritmo", label: "Ondertitel Studio", group: "Post-productie", ai: true }, // uit beta (Joris, 6 sep)
      { path: "/narrowcasting", label: "Narrowcasting", group: "Distributie", beta: true },
      { path: "/video-search", label: "Videotheek", group: "AI Tools", ai: true, beta: true },
      { path: "/explainer-script", label: "Explainer Studio", group: "AI Tools", ai: true },
      { path: "/scriptstijlen", label: "Scriptstijlen", group: "AI Tools", ai: true },
      { path: "/effie-guide", label: "Effie Gids", group: "AI Tools", ai: true },
      { path: "/video-downloader", label: "Downloader", group: "Hulpmiddelen", tier: "utility", beta: true },
      { path: "/pptx-video-replacer", label: "PPTX Replacer", group: "Hulpmiddelen", tier: "utility", beta: true },
      { path: "/pdf-combiner", label: "PDF Combiner", group: "Hulpmiddelen", tier: "utility" },
      { path: "/color-converter", label: "Kleur Converter", group: "Hulpmiddelen", tier: "utility" },
      { path: "/qr-generator", label: "QR-generator", group: "Hulpmiddelen", tier: "utility" },
    ],
  },
  ai: {
    label: "AI",
    hubPath: "/ai-hub",
    paths: ["/ai-hub", "/ai-chat", "/gpt-maker", "/inspiration", "/avatar-chat", "/ai-beelden"],
    items: [
      { path: "/ai-hub", label: "Overzicht" },
      { path: "/ai-chat", label: "AI chat project", group: "Werkproces" },
      { path: "/gpt-maker", label: "GPT Bouwer", group: "Werkproces" },
      { path: "/inspiration", label: "Inspiratie", group: "Hulpmiddelen", tier: "utility", ai: true },
      { path: "/avatar-chat", label: "Avatar Chat", group: "Hulpmiddelen", tier: "utility", ai: true },
      { path: "/ai-beelden", label: "AI-beelden", group: "Hulpmiddelen", tier: "utility", ai: true, beta: true },
    ],
  },
  operations: {
    label: "Operations",
    hubPath: "/operations",
    paths: ["/operations", "/joris-dashboard", "/strategy", "/sales-dashboard", "/sales/prospects", "/offertes", "/achmea-export", "/revenue-forecast", "/budget", "/opdracht-mix", "/ohw-check", "/uren-check", "/bbi-doel", "/enquete-dashboard", "/declarabiliteit", "/leveranciers", "/freelancers/mailadressen", "/werving"],
    items: [
      { path: "/operations", label: "Overzicht" },
      { path: "/joris-dashboard", label: "Werkdruk PL's & team", feature: "kpi_dashboard", group: "Sturing" },
      { path: "/declarabiliteit", label: "Declarabiliteit", feature: "kpi_dashboard", group: "Sturing", beta: true },
      { path: "/strategy", label: "Strategie", feature: "strategy", group: "Strategie" },
      { path: "/sales-dashboard", label: "Sales", feature: "sales_dashboard", group: "Commercieel" },
      { path: "/sales/prospects", label: "Prospects", feature: "sales_prospects", group: "Commercieel" },
      { path: "/offertes", label: "Offerte Generator", feature: "offertes", group: "Commercieel" },
      { path: "/achmea-export", label: "Achmea factuur-export", feature: "offertes", group: "Commercieel", beta: true },
      { path: "/revenue-forecast", label: "Forecast", feature: "revenue_forecast", group: "Commercieel" },
      { path: "/budget", label: "Budget", feature: "budget", group: "Commercieel", beta: true },
      { path: "/opdracht-mix", label: "Opdracht-mix", feature: "kpi_dashboard", group: "Sturing", beta: true },
      { path: "/ohw-check", label: "OHW", feature: "kpi_dashboard", group: "Financieel" },
      { path: "/uren-check", label: "Uren", feature: "kpi_dashboard", group: "Financieel" },
      { path: "/bbi-doel", label: "BBI-doel", feature: "kpi_dashboard", group: "Financieel", beta: true },
      { path: "/enquete-dashboard", label: "Klanttevredenheid", feature: "enquete_dashboard", group: "Kwaliteit" },
      { path: "/leveranciers", label: "Leveranciers", feature: "leveranciers", group: "Mensen", beta: true },
      { path: "/freelancers/mailadressen", label: "Freelancers · Mailadressen", feature: "freelancer_mail", group: "Mensen", beta: true },
      { path: "/werving", label: "Werving", feature: "werving", group: "Mensen", beta: true },
    ],
  },
  lab: {
    label: "Lab",
    hubPath: "/lab",
    paths: ["/lab", "/lab/ahlstrom-interactive", "/website-preview"],
    items: [
      { path: "/lab", label: "Overzicht" },
      { path: "/lab/ahlstrom-interactive", label: "Ahlstrom Interactive", group: "Experimenten", beta: true },
      { path: "/website-preview", label: "Website Preview", feature: "website_feedback", group: "Experimenten", beta: true },
    ],
  },
};

// -- Layout metadata (single source of truth voor App.js + AppShell) ----------
// Twee aparte concepten die eerder als losse hardcoded lijsten uiteenliepen:
//  1. Full-bleed: content krijgt géén gecentreerde grid-wrapper (App.js).
//  2. Full-screen: echte editors — verbergen óók hoofd-sidebar + secundaire nav.

const FULL_BLEED_PATHS = [
  "/", "/beta", "/vandaag", "/ai-chat", "/gpt-maker", "/accounts",
  "/strategy", "/sales/prospects", "/MaterialReservation",
  "/name-title-generator", "/mijn-profiel", "/offertes",
  "/color-converter", "/qr-generator", "/planning/voertuigen",
];
const FULL_BLEED_PREFIXES = ["/accounts/", "/materiaal-beheer", "/planning/corvee", "/MeetingDashboard/", "/overleg/sessie/"];

/** Content zonder gecentreerde wrapper (full-bleed pagina's). */
export function isFullBleedPath(pathname) {
  return (
    FULL_BLEED_PATHS.includes(pathname) ||
    FULL_BLEED_PREFIXES.some((p) => pathname.startsWith(p))
  );
}

/** Echte full-screen editors: verbergen zowel hoofd-sidebar als secundaire nav. */
const FULL_SCREEN_PATHS = ["/ai-chat", "/gpt-maker"];
export function isFullScreenPath(pathname) {
  return FULL_SCREEN_PATHS.includes(pathname);
}

/**
 * DESIGN-PRINCIPE: werk-/detailpagina's tonen géén secundaire sub-nav — die
 * ruimte gaat naar het werk zelf, of naar een tool-eigen linkerkolom (offertes:
 * gesprekken, meetings: je meeting-lijst). De hoofd-sidebar blijft altijd staan.
 * Zie policies/ff-design.md.
 */
export function hidesSecondaryNav(pathname) {
  return (
    isFullScreenPath(pathname) ||
    /^\/callsheets(\/.+)?$/.test(pathname) ||
    pathname === "/offertes" ||
    // Mijn To-Do's heeft een eigen lijsten-kolom op die plek (2 sep)
    pathname === "/mijn-todos" ||
    pathname.startsWith("/MeetingDashboard/") ||
    // Overlegpagina heeft een eigen sessies-kolom op die plek
    pathname.startsWith("/overleg/sessie/") ||
    // Sollicitantkaart heeft een eigen sollicitanten-kolom op die plek (werving sprint 1)
    pathname.startsWith("/werving/sollicitatie/")
  );
}

/** Hub-/landingspagina's: topbar-titel verbergen — body header is leidend. */
export function isHubPath(pathname) {
  if (pathname === "/" || pathname === "/projects") return true;
  return Object.values(SECONDARY_NAV_SECTIONS).some((s) => s.hubPath === pathname);
}

/**
 * Get secondary nav for a pathname.
 * Shows on hub overview pages and their direct child routes.
 * Collapses on tool pages so content gets full width.
 */
export function getSecondaryNav(pathname, permissions = {}) {
  const section = Object.values(SECONDARY_NAV_SECTIONS).find((candidate) =>
    candidate.paths.some((p) => pathname === p || pathname.startsWith(p + "/"))
  );

  if (!section) return null;

  return {
    label: section.label,
    items: section.items.filter((item) => !item.feature || permissions[item.feature]?.view),
  };
}

/**
 * Check if secondary nav should be shown as collapsed (icon-only) on this page.
 * Hub overviews show the full nav; tool pages collapse it to a thin strip.
 */
export function isSecondaryNavCollapsed(pathname) {
  // Never auto-collapse — user controls collapse state manually
  return false;
}
